import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useAnimation, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ShoppingCart, Star, TrendingUp, Flame } from 'lucide-react';

function Products({ isMobile, featuredProducts }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const controls = useAnimation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // If featuredProducts is not provided, use default sample data
  const products = featuredProducts || [
    {
      id: 1,
      name: 'Premium Pork Belly',
      description: 'Perfectly marbled with rich fat layers for crispy chicharrón or tender braising',
      image: 'https://images.unsplash.com/photo-1602476526006-1c7d0d8b143e?auto=format&fit=crop&w=800&q=80',
      price: '$24.99/kg',
      category: 'pork',
      rating: 4.9,
      tags: ['Best Seller', 'Chef\'s Choice']
    },
    {
      id: 2,
      name: 'Wagyu Beef Ribeye',
      description: 'A5 grade Japanese Wagyu with exceptional marbling and melt-in-your-mouth texture',
      image: 'https://images.unsplash.com/photo-1546833999-b9f581a1996d?auto=format&fit=crop&w=800&q=80',
      price: '$89.99/kg',
      category: 'beef',
      rating: 4.8,
      tags: ['Premium', 'Limited Stock']
    },
    {
      id: 3,
      name: 'Free-Range Chicken',
      description: 'Organic free-range chicken with firm texture and rich flavor',
      image: 'https://images.unsplash.com/photo-1562967916-eb82221dfb92?auto=format&fit=crop&w=800&q=80',
      price: '$18.99/kg',
      category: 'chicken',
      rating: 4.7,
      tags: ['Organic', 'Fresh Daily']
    },
  ];

  const categories = [
    { id: 'all', name: 'All Products', icon: '🥩', count: products.length },
    { id: 'pork', name: 'Pork Cuts', icon: '🐷', count: products.filter(p => p.category === 'pork').length },
    { id: 'beef', name: 'Beef Selection', icon: '🐮', count: products.filter(p => p.category === 'beef').length },
    { id: 'chicken', name: 'Poultry', icon: '🐔', count: products.filter(p => p.category === 'chicken').length },
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    },
    hover: {
      y: -15,
      scale: 1.02,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const floatAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % filteredProducts.length);
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + filteredProducts.length) % filteredProducts.length);
  };

  return (
    <section 
      id="products" 
      className="relative py-24 md:py-32 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)'
      }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-amber-900/5 to-red-900/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-red-900/5 to-amber-900/5 rounded-full blur-3xl"></div>
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '80px 80px',
            animation: 'gridMove 20s linear infinite'
          }}></div>
        </div>
      </div>

      {/* Floating Icons */}
      <motion.div 
        animate={floatAnimation}
        className="absolute top-10 left-5 text-4xl opacity-10"
      >
        🥓
      </motion.div>
      <motion.div 
        animate={{ ...floatAnimation, transition: { ...floatAnimation.transition, delay: 0.5 } }}
        className="absolute top-32 right-10 text-5xl opacity-10"
      >
        🍗
      </motion.div>
      <motion.div 
        animate={{ ...floatAnimation, transition: { ...floatAnimation.transition, delay: 1 } }}
        className="absolute bottom-20 left-20 text-4xl opacity-10"
      >
        🥩
      </motion.div>

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        {/* Header Section */}
        <motion.div
          variants={titleVariants}
          initial="hidden"
          animate={controls}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-red-500 rounded-full blur-md"></div>
              <div className="relative bg-gradient-to-r from-amber-500 to-red-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                PREMIUM SELECTION
              </div>
            </div>
            <Flame className="w-6 h-6 text-amber-500 animate-pulse" />
          </div>

          <h2 className={`font-bold mb-6 ${isMobile ? 'text-4xl' : 'text-6xl'} bg-gradient-to-r from-amber-200 via-amber-100 to-red-200 bg-clip-text text-transparent`}>
            Our Featured Products
          </h2>
          
          <motion.p 
            variants={titleVariants}
            className="text-gray-400 text-lg max-w-2xl mx-auto mb-12"
          >
            Hand-selected cuts from premium sources, aged to perfection and prepared by master butchers
          </motion.p>

          {/* Category Filter */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            {categories.map((category) => (
              <motion.button
                key={category.id}
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-3 ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-amber-500 to-red-500 text-white shadow-lg shadow-amber-500/25'
                    : 'bg-gray-900 text-gray-300 hover:bg-gray-800 border border-gray-800'
                }`}
              >
                <span className="text-xl">{category.icon}</span>
                <span>{category.name}</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  selectedCategory === category.id
                    ? 'bg-white/20'
                    : 'bg-gray-800'
                }`}>
                  {category.count}
                </span>
              </motion.button>
            ))}
          </motion.div>
        </motion.div>

        {/* Mobile Carousel */}
        {isMobile ? (
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.3 }}
                className="mb-8"
              >
                <ProductCard 
                  product={filteredProducts[currentSlide]} 
                  isMobile={isMobile}
                  onHover={() => setHoveredProduct(filteredProducts[currentSlide].id)}
                  isHovered={hoveredProduct === filteredProducts[currentSlide].id}
                />
              </motion.div>
            </AnimatePresence>
            
            <div className="flex justify-center items-center gap-6 mt-8">
              <button
                onClick={handlePrevSlide}
                className="p-3 rounded-full bg-gradient-to-r from-amber-900 to-amber-800 text-white hover:shadow-lg hover:shadow-amber-500/25 transition-all"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <div className="flex gap-2">
                {filteredProducts.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentSlide
                        ? 'bg-gradient-to-r from-amber-500 to-red-500 w-8'
                        : 'bg-gray-700'
                    }`}
                  />
                ))}
              </div>
              
              <button
                onClick={handleNextSlide}
                className="p-3 rounded-full bg-gradient-to-r from-red-900 to-red-800 text-white hover:shadow-lg hover:shadow-red-500/25 transition-all"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        ) : (
          /* Desktop Grid */
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-8`}
          >
            {filteredProducts.map((product) => (
              <motion.div
                key={product.id}
                variants={itemVariants}
                whileHover="hover"
                onMouseEnter={() => setHoveredProduct(product.id)}
                onMouseLeave={() => setHoveredProduct(null)}
              >
                <ProductCard 
                  product={product} 
                  isMobile={isMobile}
                  isHovered={hoveredProduct === product.id}
                />
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Stats Banner */}
      {/*  <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="mt-20 bg-gradient-to-r from-gray-900 to-black rounded-2xl p-8 border border-gray-800 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-amber-500/10 to-transparent rounded-full -translate-x-16 -translate-y-16"></div>
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-red-500/10 to-transparent rounded-full translate-x-16 translate-y-16"></div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 relative z-10">
            {[
              { label: 'Products Available', value: '50+', icon: '🥩' },
              { label: 'Happy Customers', value: '2K+', icon: '😊' },
              { label: 'Delivery Areas', value: '15+', icon: '🚚' },
              { label: 'Quality Rating', value: '4.9/5', icon: '⭐' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center"
              >
                <div className="text-4xl mb-2">{stat.icon}</div>
                <div className="text-3xl font-bold bg-gradient-to-r from-amber-300 to-red-300 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        */}
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes gridMove {
          0% { transform: translateY(0) translateX(0); }
          100% { transform: translateY(80px) translateX(80px); }
        }
        
        .gradient-border {
          border: 2px solid transparent;
          background: 
            linear-gradient(black, black) padding-box,
            linear-gradient(45deg, #fbbf24, #dc2626) border-box;
        }
        
        .product-card-glow {
          box-shadow: 
            0 10px 40px rgba(251, 191, 36, 0.1),
            0 5px 15px rgba(220, 38, 38, 0.05);
        }
        
        .product-card-glow:hover {
          box-shadow: 
            0 25px 50px rgba(251, 191, 36, 0.2),
            0 15px 30px rgba(220, 38, 38, 0.15);
        }
      `}</style>
    </section>
  );
}

// Product Card Component
const ProductCard = ({ product, isMobile, isHovered }) => {
  return (
    <div className="relative group h-full">
      {/* Glow Effect */}
      <div className={`absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-red-500/20 to-amber-500/20 rounded-3xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${isHovered ? 'opacity-100' : ''}`}></div>
      
      {/* Main Card */}
      <div className="relative h-full bg-gradient-to-b from-gray-900 to-black rounded-2xl overflow-hidden border border-gray-800 product-card-glow transition-all duration-300">
        {/* Image Container */}
        <div className="relative h-64 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10"></div>
          <img 
            src={product.image} 
            alt={product.name} 
            className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
          />
          
          {/* Badges */}
          <div className="absolute top-4 left-4 z-20 flex flex-wrap gap-2">
            {product.tags?.map((tag, index) => (
              <div 
                key={index}
                className="px-3 py-1 text-xs font-bold rounded-full bg-gradient-to-r from-amber-600 to-red-600 text-white"
              >
                {tag}
              </div>
            ))}
          </div>
          
          {/* Rating */}
          <div className="absolute top-4 right-4 z-20 flex items-center gap-1 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full">
            <Star className="w-4 h-4 text-amber-500 fill-current" />
            <span className="text-white text-sm font-bold">{product.rating}</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex justify-between items-start mb-3">
            <h3 className="text-xl font-bold text-white">{product.name}</h3>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: isHovered ? 1 : 0 }}
              transition={{ type: "spring", stiffness: 500 }}
              className="text-2xl text-amber-500"
            >
              🔥
            </motion.div>
          </div>
          
          <p className="text-gray-400 mb-4 line-clamp-2">{product.description}</p>
          
          {/* Price and Action */}
          <div className="flex justify-between items-center mt-6">
            <div>
              <div className="text-sm text-gray-500">Starting from</div>
              <div className="text-2xl font-bold bg-gradient-to-r from-amber-300 to-red-300 bg-clip-text text-transparent">
                {product.price || '$24.99/kg'}
              </div>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-600 to-red-600 text-white font-semibold flex items-center gap-2 hover:shadow-lg hover:shadow-amber-500/25 transition-all"
            >
              <ShoppingCart className="w-4 h-4" />
              Order Now
            </motion.button>
          </div>

          {/* Additional Info */}
          <div className="mt-6 pt-6 border-t border-gray-800 flex justify-between text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span>High Demand</span>
            </div>
            <div>📦 Free Delivery</div>
            <div>📅 Order Today</div>
          </div>
        </div>

        {/* Hover Effect Line */}
        <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-red-500 transform origin-left transition-transform duration-300 ${isHovered ? 'scale-x-100' : 'scale-x-0'}`}></div>
      </div>
    </div>
  );
};

export default Products;